import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../api/client';

const AUTO_REFRESH_MS = 10 * 60 * 1000;

type SyncVenue = {
  venue: string;
  type: string;
  method: string;
  status: string;
  interval_min: number;
  freshness_min: number;
  full_sync_age_min: number;
  last_error: string | null;
  last_account_email: string | null;
  runtime_ms: number | null;
};

type SyncSummary = {
  total: number;
  succeeded: number;
  failed: number;
  in_flight: number;
  avg_min: number;
  median_min: number;
  p95_min: number;
  freshest_min: number;
  stalest_min: number;
};

type SyncFailure = {
  venue: string;
  external_id: string;
  subdomain: string | null;
  last_error: string | null;
};

type P95Entry = {
  venue: string;
  type: string;
  method: string;
  status: string;
  interval_min: number;
  freshness_min: number;
  last_error: string | null;
};

type IntervalSummary = SyncSummary & { interval_min: number };

type HealthData = {
  timestamp: string;
  sync: {
    venues: SyncVenue[];
    summary: SyncSummary;
    summaryByInterval: IntervalSummary[];
    paused: number;
    disabled: number;
    failures: SyncFailure[];
    p95Slowest: P95Entry[];
  };
};

function StatusBadge({ status }: { status: string }) {
  const colorMap: Record<string, string> = {
    succeeded: '#22c55e',
    syncing: '#3b82f6',
    queued: '#f59e0b',
    failed: '#ef4444',
    idle: '#9ca3af',
  };
  const color = colorMap[status] || '#9ca3af';
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: '12px',
      fontSize: '11px',
      fontWeight: 600,
      color: '#fff',
      background: color,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    }}>
      {status}
    </span>
  );
}

function StatCard({ label, value, unit, color }: { label: string; value: string | number; unit?: string; color?: string }) {
  return (
    <div style={{
      background: '#fff',
      border: '1px solid var(--gray-200)',
      borderRadius: '8px',
      padding: '16px 20px',
      flex: '1 1 0',
      minWidth: '140px',
    }}>
      <div style={{ fontSize: '12px', color: 'var(--gray-500)', fontWeight: 500, marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        {label}
      </div>
      <div style={{ fontSize: '28px', fontWeight: 700, color: color || 'var(--gray-900)', lineHeight: 1.1 }}>
        {value}{unit && <span style={{ fontSize: '14px', fontWeight: 500, color: 'var(--gray-400)', marginLeft: '2px' }}>{unit}</span>}
      </div>
    </div>
  );
}

function freshnessColor(min: number): string {
  if (min <= 5) return 'var(--gray-900)';
  if (min <= 10) return '#f59e0b';
  return '#ef4444';
}

export default function SystemHealthScreen() {
  const [data, setData] = useState<HealthData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchHealth = useCallback(async () => {
    try {
      setError(null);
      const result = await api.getSystemHealth();
      setData(result);
      setLastRefresh(new Date());
    } catch (err: any) {
      setError(err.message || 'Failed to fetch system health');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHealth();
    intervalRef.current = setInterval(fetchHealth, AUTO_REFRESH_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [fetchHealth]);

  const handleRefresh = () => {
    setLoading(true);
    fetchHealth();
  };

  return (
    <>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '24px', color: 'var(--gray-900)' }}>System Health</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--gray-500)', fontSize: '14px' }}>Integration sync status and infrastructure monitoring</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {lastRefresh && (
            <span style={{ fontSize: '12px', color: 'var(--gray-400)' }}>
              Updated {lastRefresh.toLocaleTimeString()}
            </span>
          )}
          <button className="btn btn-primary btn-sm" onClick={handleRefresh} disabled={loading}>
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && <div className="alert alert-error" style={{ marginBottom: '16px' }}>{error}</div>}

      {loading && !data ? (
        <div className="loading"><div className="spinner"></div></div>
      ) : data ? (
        <>
          {/* Summary Stats */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
            <StatCard label="Total Active" value={data.sync.summary.total} />
            <StatCard label="Succeeded" value={data.sync.summary.succeeded} color="#22c55e" />
            <StatCard label="Failed" value={data.sync.summary.failed} color={data.sync.summary.failed > 0 ? '#ef4444' : '#22c55e'} />
            <StatCard label="In-Flight" value={data.sync.summary.in_flight} color="#3b82f6" />
            <StatCard label="Disabled" value={data.sync.disabled} color={data.sync.disabled > 0 ? '#6b7280' : 'var(--gray-900)'} />
            <StatCard label="Paused" value={data.sync.paused} color={data.sync.paused > 0 ? '#f59e0b' : 'var(--gray-900)'} />
          </div>

          {/* Freshness by Refresh Interval */}
          {(data.sync.summaryByInterval || []).map((g) => (
            <div key={g.interval_min} style={{ marginBottom: '20px' }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--gray-600)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {g.interval_min}-min refresh interval ({g.total} integrations)
              </div>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <StatCard label="Avg Freshness" value={g.avg_min} unit="min" />
                <StatCard label="Median" value={g.median_min} unit="min" />
                <StatCard label="P95" value={g.p95_min} unit="min" color={Number(g.p95_min) > g.interval_min * 1.2 ? '#ef4444' : 'var(--gray-900)'} />
                <StatCard label="Freshest" value={g.freshest_min} unit="min" color="#22c55e" />
                <StatCard label="Stalest" value={g.stalest_min} unit="min" color={Number(g.stalest_min) > g.interval_min ? '#ef4444' : freshnessColor(Number(g.stalest_min))} />
              </div>
            </div>
          ))}

          {/* Failures */}
          {data.sync.failures.length > 0 && (
            <div className="card" style={{ marginBottom: '20px', borderLeft: '4px solid #ef4444' }}>
              <h3 style={{ margin: '0 0 12px', color: '#ef4444' }}>Failed Integrations</h3>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--gray-200)', textAlign: 'left' }}>
                    <th style={{ padding: '8px 12px' }}>Venue</th>
                    <th style={{ padding: '8px 12px' }}>Subdomain</th>
                    <th style={{ padding: '8px 12px' }}>Error</th>
                  </tr>
                </thead>
                <tbody>
                  {data.sync.failures.map((f, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                      <td style={{ padding: '8px 12px', fontWeight: 500 }}>{f.venue}</td>
                      <td style={{ padding: '8px 12px', color: 'var(--gray-500)' }}>{f.subdomain || '-'}</td>
                      <td style={{ padding: '8px 12px', color: '#ef4444', fontFamily: 'monospace', fontSize: '11px' }}>{f.last_error || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* P95 Slowest */}
          {data.sync.p95Slowest.length > 0 && (
            <div className="card" style={{ marginBottom: '20px', borderLeft: '4px solid #f59e0b' }}>
              <h3 style={{ margin: '0 0 12px', color: '#f59e0b' }}>P95 Slowest Syncs</h3>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--gray-200)', textAlign: 'left' }}>
                    <th style={{ padding: '8px 12px' }}>Venue</th>
                    <th style={{ padding: '8px 12px' }}>Type</th>
                    <th style={{ padding: '8px 12px' }}>Status</th>
                    <th style={{ padding: '8px 12px' }}>Interval</th>
                    <th style={{ padding: '8px 12px' }}>Freshness</th>
                  </tr>
                </thead>
                <tbody>
                  {data.sync.p95Slowest.map((v, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                      <td style={{ padding: '8px 12px', fontWeight: 500 }}>{v.venue}</td>
                      <td style={{ padding: '8px 12px', color: 'var(--gray-500)' }}>{v.type}/{v.method}</td>
                      <td style={{ padding: '8px 12px' }}><StatusBadge status={v.status} /></td>
                      <td style={{ padding: '8px 12px', color: 'var(--gray-500)', fontVariantNumeric: 'tabular-nums' }}>{v.interval_min}m</td>
                      <td style={{ padding: '8px 12px', fontWeight: 600, color: v.freshness_min > v.interval_min ? '#ef4444' : freshnessColor(v.freshness_min) }}>{v.freshness_min} min</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Full Venue Table */}
          <div className="card">
            <h3 style={{ margin: '0 0 12px' }}>All Integrations ({data.sync.venues.length})</h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--gray-200)', textAlign: 'left' }}>
                    <th style={{ padding: '8px 12px' }}>Venue</th>
                    <th style={{ padding: '8px 12px' }}>Type</th>
                    <th style={{ padding: '8px 12px' }}>Method</th>
                    <th style={{ padding: '8px 12px' }}>Status</th>
                    <th style={{ padding: '8px 12px' }}>Interval</th>
                    <th style={{ padding: '8px 12px' }}>Freshness</th>
                    <th style={{ padding: '8px 12px' }}>Full Sync Age</th>
                    <th style={{ padding: '8px 12px' }}>Runtime</th>
                    <th style={{ padding: '8px 12px' }}>Login</th>
                    <th style={{ padding: '8px 12px' }}>Error</th>
                  </tr>
                </thead>
                <tbody>
                  {data.sync.venues.map((v, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--gray-100)' }}>
                      <td style={{ padding: '8px 12px', fontWeight: 500, whiteSpace: 'nowrap' }}>{v.venue}</td>
                      <td style={{ padding: '8px 12px', color: 'var(--gray-500)' }}>{v.type}</td>
                      <td style={{ padding: '8px 12px', color: 'var(--gray-500)' }}>{v.method}</td>
                      <td style={{ padding: '8px 12px' }}><StatusBadge status={v.status} /></td>
                      <td style={{ padding: '8px 12px', color: 'var(--gray-500)', fontVariantNumeric: 'tabular-nums' }}>
                        {v.interval_min}m
                      </td>
                      <td style={{ padding: '8px 12px', fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: v.freshness_min > v.interval_min ? '#ef4444' : freshnessColor(v.freshness_min) }}>
                        {v.freshness_min} min
                      </td>
                      <td style={{ padding: '8px 12px', color: 'var(--gray-500)', fontVariantNumeric: 'tabular-nums' }}>
                        {v.full_sync_age_min} min
                      </td>
                      <td style={{ padding: '8px 12px', color: 'var(--gray-500)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                        {v.runtime_ms ? (v.runtime_ms / 1000).toFixed(1) + 's' : <span style={{ color: 'var(--gray-300)' }}>—</span>}
                      </td>
                      <td style={{ padding: '8px 12px', color: 'var(--gray-500)', fontSize: '11px', whiteSpace: 'nowrap' }}>
                        {v.last_account_email
                          ? v.last_account_email.replace(/@.*/, (m: string) => m.length > 10 ? '@…' : m)
                          : <span style={{ color: 'var(--gray-300)' }}>—</span>}
                      </td>
                      <td style={{ padding: '8px 12px', color: '#ef4444', fontFamily: 'monospace', fontSize: '11px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {v.last_error || ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div style={{ marginTop: '16px', textAlign: 'center', fontSize: '12px', color: 'var(--gray-400)' }}>
            Auto-refreshes every 10 minutes
          </div>
        </>
      ) : null}
    </>
  );
}
